/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable import/no-unresolved */
import { useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  Slide,
  Backdrop,
  Stack,
  Button,
  IconButton,
  Grid,
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';

import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { selectOrder } from '@/store/selectors';
import { setAlert } from '@/store/slices/alert';
import Input from '@/components/Forms/Input';
import { validationSchema } from '@/utils/helpers';
import { ScrollBar } from '@/styles/global';
import { useFetchPosProfileDetails } from '@/services/hooks';

const boxStyle = {
  position: 'absolute',
  height: 'max-content',
  width: '500px',
  backgroundColor: '#fff',
  overflow: 'auto',
  borderRadius: '15px',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  margin: 'auto',
  textAlign: 'center',
  py: 4,
  px: 4,
  ...ScrollBar,
};

export type ChangeObjectType = {
  answer: 'yes' | 'no';
  amountGiven: number;
  amountSaved: number;
};
interface SaveChangeProps {
  show: boolean;
  handleModalClose: () => void;
  handleSaveChangeAction: (changeObject: ChangeObjectType) => void;
}
const SaveChange: React.FC<SaveChangeProps> = ({
  show,
  handleModalClose,
  handleSaveChangeAction,
}) => {
  const { change } = useSelector(selectOrder);
  const dispatch = useDispatch();
  const { data: profile } = useFetchPosProfileDetails();

  const initialState = { amountSaved: '', amountGiven: '' };
  const { dynamic } = validationSchema;

  const [showForm, setShowForm] = useState(false);

  const handleClose = () => {
    setShowForm(false);

    handleModalClose();
  };

  const handleSubmit = (values: typeof initialState) => {
    const changeObject: ChangeObjectType = {
      answer: 'yes',
      amountGiven: +values.amountGiven,
      amountSaved: +values.amountSaved,
    };

    if (changeObject.amountGiven + changeObject.amountSaved > change) {
      dispatch(
        setAlert({
          type: 'error',
          message:
            'Total change breakdown must not be greater than customer change',
        }),
      );

      return;
    }

    handleSaveChangeAction(changeObject);
  };

  const handleGiveChange = () => {
    const changeObject: ChangeObjectType = {
      answer: 'no',
      amountGiven: 0,
      amountSaved: 0,
    };

    handleSaveChangeAction(changeObject);
  };

  return (
    <div>
      <Modal
        open={show}
        onClose={handleClose}
        aria-labelledby="logout-modal-title"
        aria-describedby="logout-modal-description"
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Slide direction="up" in={show} mountOnEnter unmountOnExit>
          <Box sx={boxStyle}>
            <Box sx={{ textAlign: 'left', marginBottom: 6 }}>
              <IconButton
                onClick={handleClose}
                sx={{
                  width: '92px',
                  height: '40px',
                  borderRadius: '90px',
                  p: '12px 16px',
                  position: 'absolute',
                  left: 0,
                  top: '10px',
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    marginLeft: 1,
                    fontFamily: 'Montserrat',
                    color: (theme) => theme.palette.primary.main,
                  }}
                >
                  Close
                </Typography>
              </IconButton>
            </Box>
            <Typography
              sx={{ fontSize: '20px', fontWeight: 700, color: '#0043B9' }}
            >
              Save Change?
            </Typography>
            {/* INITIAL QUESTION ASKED */}
            {!showForm && (
              <Box>
                <Typography
                  sx={{ fontSize: '15px', color: '#252525', marginTop: 2 }}
                >
                  Save customer change? This can be used in their next
                  transaction with you.
                </Typography>

                <Stack mt={15} width="90%" mx="auto" spacing={2}>
                  <Button
                    variant="contained"
                    onClick={() => setShowForm(true)}
                    disableElevation
                  >
                    Yes, save
                  </Button>
                  <Button
                    onClick={() => handleGiveChange()}
                    variant="outlined"
                    sx={{
                      backgroundColor: 'transparent',
                      border: '1px solid #C1C7D0',
                      color: '#252525',
                      '&:hover': {
                        backgroundColor: '#C1C7D0',
                        border: '1px solid transparent',
                      },
                    }}
                  >
                    No, give change
                  </Button>
                </Stack>
              </Box>
            )}

            {/* FORM TO SAVE CHANGE */}
            {showForm && (
              <Box>
                <Typography
                  sx={{ fontSize: '15px', color: '#252525', marginTop: 2 }}
                >
                  Input the amount saved and amount given out of the change
                </Typography>

                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: 700,
                    color: '#0043B9',
                    mt: 2,
                  }}
                >
                  Change: {profile?.currency} {change.toLocaleString('en-US')}
                </Typography>
                <Formik
                  initialValues={initialState}
                  onSubmit={handleSubmit}
                  validationSchema={yup.object({
                    amountSaved: dynamic('Amount to Save'),
                    amountGiven: dynamic('Amount given'),
                  })}
                >
                  <Form>
                    <Grid
                      container
                      spacing={4}
                      marginBottom={2}
                      marginTop={0.5}
                    >
                      <Input
                        type="text"
                        label="Amount given"
                        name="amountGiven"
                        size={12}
                      />
                      <Input
                        type="text"
                        label="Amount to Save"
                        name="amountSaved"
                        size={12}
                      />
                    </Grid>

                    <Stack mt={5} width="70%" mx="auto" spacing={2}>
                      <Button
                        variant="contained"
                        type="submit"
                        disableElevation
                      >
                        Save
                      </Button>
                    </Stack>
                  </Form>
                </Formik>
              </Box>
            )}
          </Box>
        </Slide>
      </Modal>
    </div>
  );
};

export default SaveChange;
